import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  Input,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  VStack,
  useDisclosure,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress
} from '@chakra-ui/react';
import {

  FiUsers,
  FiTag,
  FiFilter,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiDownload,
  FiUpload,
  FiChevronDown,
  FiFileText
} from 'react-icons/fi';
import Select from 'react-select';
import Header from '../../components/Header';
import { fetchWrapper } from '../../helpers';
import { CheckCircleIcon, CloseIcon } from '@chakra-ui/icons';
import { toast } from 'react-toastify';
import API from '../../helpers/axiosInstance';
import { hasPermission } from '../../utils/DefaultFunction';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { ContactsTable } from './ContactTable';
import { SearchAndFilter } from './Filters';

// ============= API UTILITIES =============
const contactsAPI = {
  getTags: () => {
    const url = `${process.env.REACT_APP_API_URL}/tags`;
    return fetchWrapper.get(url);
  },

  createTag: (tagData) => {
    const url = `${process.env.REACT_APP_API_URL}/tags`;
    return fetchWrapper.post(url, tagData);
  },

  deleteTag: (tagId) => {
    const url = `${process.env.REACT_APP_API_URL}/tags/${tagId}`;
    return fetchWrapper.delete(url);
  },

  getContacts: (query = '', page = 1, limit = 10, tagId = 'all', whatsapp = 'all', subscriber = 'all', cohortId = 'all') => {
    const params = new URLSearchParams();

    // Add search query
    if (query) params.append('q', query);

    // Add pagination
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    // Add tag filter
    if (tagId && tagId !== 'all') params.append('tagId', tagId);

    // Add WhatsApp filter
    if (whatsapp && whatsapp !== 'all') params.append('whatsapp', whatsapp);

    // Add Subscriber filter
    if (subscriber && subscriber !== 'all') params.append('subscriber', subscriber);

    // Add Cohort filter
    if (cohortId && cohortId !== 'all') params.append('cohortId', cohortId);

    const url = `${process.env.REACT_APP_API_URL}/contacts?${params.toString()}`;
    return fetchWrapper.get(url);
  },

  createContact: (contactData) => {
    const url = `${process.env.REACT_APP_API_URL}/contacts`;
    return fetchWrapper.post(url, contactData);
  },

  updateContact: (contactId, contactData) => {
    const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}`;
    return fetchWrapper.put(url, contactData);
  },

  deleteContact: (contactId) => {
    const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}`;
    return fetchWrapper.delete(url);
  }
};

const getApiHeaders = () => ({
  "x-api-key": localStorage.getItem("api-key"),
  "x-access-token": localStorage.getItem("customer-portal"),
});

// ============= CONSTANTS =============
const tagColors = [
  'var(--brand-primary)', '#38B2AC', '#D69E2E', '#E53E3E', '#805AD5',
  '#0BC5EA', '#68D391', '#F56500', '#EC407A', '#5A67D8'
];

// ============= COMPONENTS =============
const StatsCard = ({ title, value, subtitle, icon: Icon, isLoading }) => (
  <Box className='box-style-card'>
    <Box>
      <Flex align="center" justify="space-between">
        <Box>
          <Text fontSize="sm" color="gray.600" mb={1}>
            {title}
          </Text>
          {isLoading ? (
            <Spinner size="md" />
          ) : (
            <Text fontSize="2xl" fontWeight="bold" mb={1}>
              {value}
            </Text>
          )}
          <Text fontSize="xs" color="gray.500">
            {subtitle}
          </Text>
        </Box>
        <Box color="gray.400">
          <Icon size={24} />
        </Box>
      </Flex>
    </Box>
  </Box>
);


const BulkTagModal = ({ isOpen, onClose, tags, selectedContacts, contacts, onAssignTags, isLoading }) => {
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [processing, setProcessing] = useState(false);

  const handleTagSelection = (tagId) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = async () => {
    if (selectedTagIds.length === 0 || selectedContacts.length === 0) return;

    setProcessing(true);
    try {
      await onAssignTags({
        contactIds: selectedContacts,
        tagIds: selectedTagIds,
      });

      setSelectedTagIds([]);
      onClose();
    } catch (error) {
      console.error('Error processing tags:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Assign Tags</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={1} align="stretch">
            <FormControl>
              <FormLabel fontSize={"xs"}>
                Assign Tags to Selected Contacts
              </FormLabel>
              <Box
                maxH="200px"
                overflowY="auto"
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
              >
                {tags.length === 0 ? (
                  <Text textAlign="center" color="gray.500" py={4}>
                    No tags available
                  </Text>
                ) : (
                  <Flex flexWrap="wrap" gap={2}>
                    {tags.map(tag => (
                      <Badge
                        key={tag._id}
                        variant={selectedTagIds.includes(tag._id) ? "solid" : "outline"}
                        colorScheme={selectedTagIds.includes(tag._id) ? "blue" : "gray"}
                        borderRadius="full"
                        px={3}
                        py={.5}
                        cursor="pointer"
                        onClick={() => handleTagSelection(tag._id)}
                        _hover={{
                          transform: 'scale(1.05)',
                          boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        fontSize={"xs"}
                      >
                        {tag.name}
                      </Badge>
                    ))}
                  </Flex>
                )}
              </Box>
            </FormControl>
          </VStack>

          <Flex justify="flex-end" gap={3} mt={6}>
            <Button
              variant="ghost"
              onClick={onClose}
              isDisabled={processing}
              size={"sm"}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={processing}
              loadingText="Assigning..."
              isDisabled={selectedTagIds.length === 0}
              size={"sm"}
            >
              Assign Tags
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/* ------------------------------------------------------------------ *
 * Contact identity rules — shared by the Add and Edit modals
 *
 * A contact needs at least one way to be reached, but either channel will do:
 * phone-only, email-only and both are all valid. Name carries no addressing
 * information, so it is optional. Matches the Contact API documentation.
 * ------------------------------------------------------------------ */

const PHONE_REGEX = /^[0-9]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True once either channel is filled in — drives the submit button. */
const hasContactChannel = (formData) =>
  Boolean(formData.phone?.trim() || formData.email?.trim());

/**
 * Validates the pair and reports the first problem. Each field is only format
 * checked when actually provided, so leaving one blank is not an error.
 * Returns true when the form may be submitted.
 */
const validateContactChannels = (formData) => {
  const phone = formData.phone?.trim();
  const email = formData.email?.trim();

  if (!phone && !email) {
    toast.warning('Enter a phone number or an email address.');
    return false;
  }
  if (phone && !PHONE_REGEX.test(phone)) {
    toast.warning('Phone number should contain only digits.');
    return false;
  }
  if (email && !EMAIL_REGEX.test(email)) {
    toast.warning('Enter a valid email address.');
    return false;
  }
  return true;
};

/** Helper text shown under both channel fields so the rule is visible up front. */
const CHANNEL_HINT = 'Provide a phone number or an email address — at least one is required.';

const AddContactModal = ({ isOpen, onClose, tags, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    selectedTags: []
  });
  const [saving, setSaving] = useState(false);

  const tagOptions = tags.map(tag => ({
    value: tag._id || tag.id,
    label: tag.name || tag.label,
    color: tag.color
  }));

  const handleSubmit = async () => {
    if (!validateContactChannels(formData)) {
      return;
    }

    setSaving(true);
    try {
      const contactData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        tags: formData.selectedTags.map(tag => tag.value)
      };

      await onSave(contactData);
      setFormData({ name: '', phone: '', email: '', selectedTags: [] });
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const customTagStyles = {
    multiValue: (styles, { data }) => ({
      ...styles,
      backgroundColor: data.color || 'var(--brand-primary)',
      color: 'white',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'white',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: 'white',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
      },
    }),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={6}>
            Create a new contact and assign tags for organization
          </Text>

          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input
                placeholder="Enter customer name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                isDisabled={saving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="923457777777"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                isDisabled={saving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                placeholder="fatima.sheikh@globalretail.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                isDisabled={saving}
              />
              <FormHelperText>{CHANNEL_HINT}</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Tags</FormLabel>
              <Select
                isMulti
                options={tagOptions}
                // styles={customTagStyles}
                placeholder="Select tags..."
                value={formData.selectedTags}
                onChange={(selected) => setFormData({ ...formData, selectedTags: selected || [] })}
                isDisabled={saving || isLoading}
              />
            </FormControl>
          </VStack>

          <Flex justify="flex-end" gap={3} mt={6}>
            <Button variant="ghost" onClick={onClose} isDisabled={saving}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={saving}
              loadingText="Adding..."
              isDisabled={!hasContactChannel(formData)}
              className='updated-chakra-button'
            >
              Add Contact
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const EditContactModal = ({ isOpen, onClose, contact, tags, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    selectedTags: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        selectedTags: contact.tags ? contact.tags.map(tag => ({
          value: tag._id || tag.id || tag,
          label: tag.name || tag.label || tag,
          color: tag.color || 'var(--brand-primary)'
        })) : []
      });
    }
  }, [contact]);

  const tagOptions = tags.map(tag => ({
    value: tag._id || tag.id,
    label: tag.name || tag.label,
    color: tag.color
  }));

  const handleSubmit = async () => {
    if (!validateContactChannels(formData)) {
      return;
    }

    setSaving(true);
    try {
      const contactData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        tags: formData.selectedTags.map(tag => tag.value)
      };

      await onSave(contact._id || contact.id, contactData);
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const customTagStyles = {
    multiValue: (styles, { data }) => ({
      ...styles,
      backgroundColor: data.color || 'var(--brand-primary)',
      color: 'white',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'white',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: 'white',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
      },
    }),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={6}>
            Update contact information and manage tags
          </Text>

          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input
                placeholder="Enter customer name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                isDisabled={saving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="+92-345-7777777"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                isDisabled={saving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                placeholder="fatima.sheikh@globalretail.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                isDisabled={saving}
              />
              <FormHelperText>{CHANNEL_HINT}</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Tags</FormLabel>
              <Select
                isMulti
                options={tagOptions}
                // styles={customTagStyles}
                placeholder="Select tags..."
                value={formData.selectedTags}
                onChange={(selected) => setFormData({ ...formData, selectedTags: selected || [] })}
                isDisabled={saving || isLoading}
              />
            </FormControl>
          </VStack>

          <Flex justify="flex-end" gap={3} mt={6}>
            <Button variant="ghost" onClick={onClose} isDisabled={saving}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={saving}
              loadingText="Updating..."
              isDisabled={!hasContactChannel(formData)}
            >
              Update Contact
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ManageTagsModal = ({ isOpen, onClose, tags, onCreateTag, onDeleteTag, isLoading }) => {
  const [newTag, setNewTag] = useState({ name: '', color: tagColors[0] });
  const [creating, setCreating] = useState(false);

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;

    setCreating(true);
    try {
      await onCreateTag({
        name: newTag.name,
        color: "white"
      });
      setNewTag({ name: '', color: tagColors[0] });
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Tags</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={6}>
            Create and manage custom tags for organizing contacts
          </Text>

          <Tabs variant="enclosed">
            <TabList>
              <Tab>Manage Tags</Tab>
              <Tab>Create Tag</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                {isLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" />
                  </Flex>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {tags.length === 0 ? (
                      <Text textAlign="center" color="gray.500" py={8}>
                        No tags created yet
                      </Text>
                    ) : (
                      tags.map((tag) => (
                        <Flex key={tag._id || tag.id} justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
                          <Flex align="center" gap={3}>

                            <Text>{tag.name || tag.label}</Text>
                          </Flex>
                          <IconButton
                            icon={<FiTrash2 size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => onDeleteTag(tag._id || tag.id)}
                          />
                        </Flex>
                      ))
                    )}
                  </VStack>
                )}
              </TabPanel>

              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Tag Name</FormLabel>
                    <Input
                      placeholder="Enter tag name"
                      value={newTag.name}
                      onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                      isDisabled={creating}
                    />
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel>Tag Color</FormLabel>
                    <HStack spacing={2} wrap="wrap">
                      {tagColors.map((color) => (
                        <Box
                          key={color}
                          w={8}
                          h={8}
                          bg={color}
                          borderRadius="full"
                          cursor="pointer"
                          border={newTag.color === color ? "3px solid" : "2px solid"}
                          borderColor={newTag.color === color ? "gray.800" : "gray.300"}
                          onClick={() => setNewTag({ ...newTag, color })}
                          _hover={{ transform: 'scale(1.1)' }}
                          transition="transform 0.2s"
                        />
                      ))}
                    </HStack>
                  </FormControl> */}

                  <Button
                    colorScheme="blue"
                    leftIcon={<FiPlus size={16} />}
                    onClick={handleCreateTag}
                    isDisabled={!newTag.name.trim()}
                    isLoading={creating}
                    loadingText="Creating..."
                  >
                    Create Tag
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// CSV import guide, served from public/ so it is same-origin — the `download`
// attribute is ignored on cross-origin URLs, which is why this is a bundled file
// rather than a link to the hosted Google Doc.
const CSV_GUIDE_FILE = `${process.env.PUBLIC_URL || ''}/xnotify_csv_guide.pdf`;

const CSVActionsMenu = ({ onUpload, onDownload, isUploading, uploadCSVPermission, downloadCSVPermission }) => {
  const inputRef = useRef();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUpload(file);
    }
    event.target.value = '';
  };

  const handleUploadClick = () => {
    if (isUploading) return;
    inputRef.current.click();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isUploading}
      />

      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<FiChevronDown />}
          colorScheme="blue"
          variant="outline"
          size={'sm'}
          isDisabled={isUploading}
        >
          CSV Actions
        </MenuButton>

        <MenuList>
          {uploadCSVPermission && (
            <MenuItem
              icon={<FiUpload />}
              onClick={handleUploadClick}
              isDisabled={isUploading}
            >
              {isUploading ? 'Upload in Progress...' : 'Upload CSV'}
            </MenuItem>
          )}
          {downloadCSVPermission && (
            <MenuItem icon={<FiDownload />} onClick={onDownload}>
              Download Sample CSV
            </MenuItem>
          )}
          {/* Downloads the bundled PDF rather than navigating. The `download`
              attribute names the saved file and only works same-origin, which the
              public/ path satisfies. */}
          <MenuItem
            as="a"
            href={CSV_GUIDE_FILE}
            download="xnotify_csv_guide.pdf"
            icon={<FiFileText />}
          >
            CSV Guide
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

const UploadProgressModal = ({ isOpen, onClose, progress }) => {
  const getProgressColor = () => {
    if (progress.stage === 'completed') return 'green';
    if (progress.stage === 'failed') return 'red';
    return 'blue';
  };

  const getStatusIcon = () => {
    if (progress.stage === 'completed') return <CheckCircleIcon color="green.500" />;
    if (progress.stage === 'failed') return <CloseIcon color="red.500" />;
    return <Spinner size="sm" />;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            {getStatusIcon()}
            <Text>CSV Upload Progress</Text>
          </HStack>
        </ModalHeader>
        {progress.stage === 'completed' && <ModalCloseButton />}

        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Box>
              <Flex justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.600">
                  {progress.message || 'Processing your file...'}
                </Text>
                <Text fontSize="sm" fontWeight="bold" color={getProgressColor() + '.600'}>
                  {progress.percentage}%
                </Text>
              </Flex>

              <Progress
                value={progress.percentage}
                colorScheme={getProgressColor()}
                size="lg"
                borderRadius="md"
                bg="gray.100"
              />
            </Box>

            <Box p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="xs" color="gray.600">
                <Text as="span" fontWeight="bold">Status:</Text> {progress.stage || 'Starting...'}
              </Text>
              {progress.uploadId && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Upload ID: {progress.uploadId}
                </Text>
              )}
            </Box>

            {progress.stage === 'failed' && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Upload failed. Please try again or check your CSV format.
                </AlertDescription>
              </Alert>
            )}

            {progress.stage === 'completed' && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  CSV uploaded successfully! Your contacts have been imported.
                </AlertDescription>
              </Alert>
            )}
          </VStack>

          {(progress.stage === 'completed' || progress.stage === 'failed') && (
            <Flex justify="flex-end" mt={6}>
              <Button
                colorScheme={progress.stage === 'completed' ? 'green' : 'gray'}
                onClick={onClose}
                size="sm"
              >
                {progress.stage === 'completed' ? 'Done' : 'Close'}
              </Button>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ============= MAIN COMPONENT =============
const ContactsManagement = ({ header }) => {
  // State
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTagFilter, setCurrentTagFilter] = useState('all');
  const [whatsappFilter, setWhatsappFilter] = useState('all');
  const [subscriberFilter, setSubscriberFilter] = useState('all');
  const [cohortFilter, setCohortFilter] = useState('all');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    tagedContact: 0
  });

  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    uploadId: null,
    stage: '',
    message: '',
    percentage: 0
  });

  // Refs
  const progressIntervalRef = useRef(null);
  const isInitialMount = useRef(true);
  const searchTimeoutRef = useRef(null);

  // Hooks
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isTagsOpen, onOpen: onTagsOpen, onClose: onTagsClose } = useDisclosure();
  const { isOpen: isBulkTagOpen, onOpen: onBulkTagOpen, onClose: onBulkTagClose } = useDisclosure();
  const toastChakra = useToast();

  // ============= INITIAL LOAD - ONLY 3 API CALLS =============
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);
      isInitialMount.current = true;
      try {
        const [contactsResponse, tagsResponse, permissionsResponse, countriesResponse] = await Promise.all([
          contactsAPI.getContacts('', 1, 10, 'all', 'all', 'all', 'all'),
          contactsAPI.getTags(),
          API.get(`${process.env.REACT_APP_API_URL}/permission/feature/list?slug=contacts`, {
            headers: getApiHeaders()
          }),
          fetchWrapper.get(`${process.env.REACT_APP_API_URL}/country/list?page=1&limit=300`)
        ]);
        // Process responses
        const contactsData = contactsResponse.data || contactsResponse.contacts || contactsResponse;
        const tagsData = tagsResponse.data || tagsResponse.tags || tagsResponse;
        const countriesData = countriesResponse.rows || countriesResponse.countries || countriesResponse || [];

        // Update pagination
        if (contactsResponse.pagination) {
          setPagination({
            currentPage: contactsResponse.pagination.currentPage || 1,
            limit: 10,
            total: contactsResponse.pagination.total || contactsData.length,
            totalPages: contactsResponse.pagination.totalPages || Math.ceil(contactsData.length / 10),
            tagedContact: contactsResponse.taggedContacts || 0
          });
        }

        // Set all state at once
        setContacts(contactsData);
        setTags(tagsData);
        setCountries(Array.isArray(countriesData) ? countriesData : []);
        setPermissions(permissionsResponse.data.data || []);


      } catch (error) {
        console.error('Error loading data:', error);
        setError(typeof error === 'string' ? error : 'Failed to load data. Please check your connection and try again.');
        toastChakra({
          title: "Error loading data",
          description: typeof error === 'string' ? error : "Please check your connection and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount


  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    loadContacts(searchTerm, 1, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter]);



  useEffect(() => {
    // Skip initial render
    if (isInitialMount.current) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchTerm.trim() !== '') {
      searchTimeoutRef.current = setTimeout(() => {
        loadContacts(searchTerm, 1, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }, 500);

    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);


  // ============= CLEAR SELECTIONS ON FILTER CHANGE =============
  useEffect(() => {
    setSelectedContacts([]);
  }, [pagination.currentPage, searchTerm, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter]);

  // ============= API FUNCTIONS =============
  const loadContacts = async (query = '', page = 1, limit = 10, tagId = 'all', whatsapp = 'all', subscriber = 'all', cohortId = 'all') => {

    try {
      setIsLoading(true);
      const response = await contactsAPI.getContacts(query, page, limit, tagId, whatsapp, subscriber, cohortId);
      const contactsData = response.data || response.contacts || response;

      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage || page,
          limit: 10,
          total: response.pagination.total || contactsData.length,
          totalPages: response.pagination.totalPages || Math.ceil(contactsData.length / limit),
          tagedContact: response.taggedContacts || 0
        });
      }

      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toastChakra({
        title: "Error loading contacts",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ============= HANDLERS =============
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const handleTagFilter = (selectedOption) => {

    setCurrentTagFilter(selectedOption.value);
    // loadContacts(searchTerm, pagination.currentPage, 10, selectedOption.value, whatsappFilter, subscriberFilter, cohortFilter);
  };

  const handleWhatsappFilter = (value) => {
    setWhatsappFilter(value);
    // loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, value, subscriberFilter, cohortFilter);
  };

  const handleSubscriberFilter = (value) => {
    setSubscriberFilter(value);
    // loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, value, cohortFilter);
  };

  const handleCohortFilter = (value) => {
    setCohortFilter(value);
    // loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, value);
  };

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    loadContacts(searchTerm, pageNumber, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);
    window.scrollTo(0, 0);
  };

  const handleSaveContact = async (contactData) => {
    try {
      await contactsAPI.createContact(contactData);

      // Only reload current view
      await loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);

      toastChakra({
        title: "Contact added successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onAddClose();
    } catch (error) {
      console.error('Error creating contact:', error);
      toastChakra({
        title: "Failed to add contact",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const handleUpdateContact = async (contactId, contactData) => {
    try {
      await contactsAPI.updateContact(contactId, contactData);

      // Only reload current view
      await loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);

      toastChakra({
        title: "Contact updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onEditClose();
    } catch (error) {
      console.error('Error updating contact:', error);
      toastChakra({
        title: "Failed to update contact",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const handleCreateTag = async (tagData) => {
    try {
      await contactsAPI.createTag(tagData);
      const tagsResponse = await contactsAPI.getTags();
      setTags(tagsResponse.data || tagsResponse.tags || tagsResponse);

      toastChakra({
        title: "Tag created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error creating tag:', error);
      toastChakra({
        title: "Failed to create tag",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await contactsAPI.deleteTag(tagId);
      const tagsResponse = await contactsAPI.getTags();
      setTags(tagsResponse.data || tagsResponse.tags || tagsResponse);

      if (currentTagFilter === tagId) {
        setCurrentTagFilter('all');
        loadContacts(searchTerm, 1, 10, 'all', whatsappFilter, subscriberFilter, cohortFilter);
      }

      toastChakra({
        title: "Tag deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting tag:', error);
      toastChakra({
        title: "Failed to delete tag",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    onEditOpen();
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactsAPI.deleteContact(contactId);

      // Only reload current view
      await loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);

      toastChakra({
        title: "Contact deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting contact:', error);
      toastChakra({
        title: "Failed to delete contact",
        description: typeof error === 'string' ? error : "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(contact => contact._id || contact.id));
    }
  };

  const handleBulkTagAssignment = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contacts/bulk/apply-tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': localStorage.getItem('api-key'),
          'x-access-token': localStorage.getItem('customer-portal'),
        },
        body: JSON.stringify({
          contactIds: data.contactIds,
          tagIds: data.tagIds,
          action: data.action
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update tags');
      }

      await loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);
      setSelectedContacts([]);

      toastChakra({
        title: `Tags added successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating tags:', error);
      toastChakra({
        title: "Failed to update tags",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ============= CSV UPLOAD FUNCTIONS =============
  const uploadContacts = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/contacts/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-api-key": localStorage.getItem('api-key'),
            "x-access-token": localStorage.getItem('customer-portal'),
          }
        }
      );

      if (response.data.success && response.data.upload_id) {
        const uploadId = response.data.upload_id;
        setUploadProgress(prev => ({
          ...prev,
          uploadId: uploadId,
          stage: 'processing',
          message: 'Processing your CSV file...',
          percentage: 10,
          isUploading: true
        }));

        startProgressPolling(uploadId);
      } else {
        throw new Error(response.data.message || 'Upload failed to start');
      }
    } catch (error) {
      let description = error.message;
      if (error.response?.data) {
        description = error.response.data.message || error.message || JSON.stringify(error.response.data);
      }

      toastChakra({
        title: "Upload failed",
        description,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const trackProgress = async (uploadId) => {
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/contacts/upload/${uploadId}/progress`,
        {
          headers: {
            "x-api-key": localStorage.getItem('api-key'),
            "x-access-token": localStorage.getItem('customer-portal'),
          }
        }
      );

      if (response.data.success) {
        const { stage, message, percentage } = response.data.data;

        setUploadProgress(prev => ({
          ...prev,
          stage,
          message,
          percentage
        }));

        if (stage === 'completed' || stage === 'failed' || percentage === 100) {
          stopProgressPolling();

          if (stage === 'completed') {
            // Only reload current view
            await loadContacts(searchTerm, pagination.currentPage, 10, currentTagFilter, whatsappFilter, subscriberFilter, cohortFilter);

            toastChakra({
              title: "Upload completed successfully!",
              description: message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          } else if (stage === 'failed') {
            toastChakra({
              title: "Upload failed",
              description: message,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      }
    } catch (error) {
      console.error('Progress tracking error:', error);

      let description = error.message;
      if (error.response?.data) {
        description = error.response.data.message || error.message;
      }

      setUploadProgress(prev => ({
        ...prev,
        stage: 'failed',
        message: `Error tracking progress: ${description}`,
        percentage: 0
      }));

      stopProgressPolling();

      toastChakra({
        title: "Progress tracking failed",
        description,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const startProgressPolling = (uploadId) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    const interval = setInterval(() => {
      trackProgress(uploadId);
    }, 10000);

    progressIntervalRef.current = interval;
    trackProgress(uploadId);
  };

  const stopProgressPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const closeProgressModal = () => {
    setUploadProgress({
      isUploading: false,
      uploadId: null,
      stage: '',
      message: '',
      percentage: 0
    });
    stopProgressPolling();
  };

  const downloadSampleCSV = () => {
    // Must match xnotify_csv_guide.pdf (public/) and the Contact API reference:
    //  - column order exactly as the guide's sample: name,email,phone,tags,…
    //  - phone is a numeric string with NO spaces, dashes or '+' prefix
    //  - tags come from the guide's documented vocabulary only (Newsletter,
    //    Promotional, Loyalty, Inactive, Lead, Verified) — invented tags here get
    //    copied into real uploads and create junk labels
    //  - customer_profile / other_contact are key:value pairs joined by '|'
    const sampleData = [
      ['name', 'email', 'phone', 'tags', 'customer_profile', 'other_contact'],
      ['Sara Khan', 'sara@example.com', '923119876543', 'Newsletter,Loyalty', 'age:25|city:Karachi', 'whatsapp:923119876543'],
      ['Ali Hassan', 'ali@example.com', '923001234567', 'Promotional', 'age:30|city:Lahore', 'facebook:https://facebook.com/ali'],
      ['Fatima Noor', 'fatima@example.com', '923214567890', 'Loyalty,Verified', 'age:34|city:Islamabad', 'whatsapp:923214567890'],
      ['Bilal Ahmed', 'bilal@example.com', '923337654321', 'Lead', 'age:28|city:Faisalabad', ''],
      ['Ayesha Siddiqui', 'ayesha@example.com', '923459876543', 'Newsletter,Promotional', 'age:41|city:Multan', 'instagram:ayesha.s'],
      ['Usman Tariq', 'usman@example.com', '923126549870', 'Inactive', 'age:37|city:Peshawar', ''],
      ['Zainab Malik', 'zainab@example.com', '923008889990', 'Verified,Loyalty', 'age:23|city:Karachi', 'whatsapp:923008889990'],
      ['Hamza Sheikh', 'hamza@example.com', '923457778880', 'Newsletter', 'age:45|city:Quetta', ''],
      ['Maryam Iqbal', 'maryam@example.com', '923336667770', 'Promotional,Lead', 'age:31|city:Hyderabad', 'facebook:maryam.iqbal'],
      // Phone-only row: everything else may be blank, per the guide.
      ['', '', '923000000001', '', '', ''],
    ];

    const csvContent = sampleData
      // Escape embedded quotes per RFC 4180 ("" inside a quoted field). Every field is
      // quoted because tags and customer_profile legitimately contain commas.
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    // Leading BOM: the sample contains non-ASCII text ("São Paulo") and Excel on
    // Windows ignores the charset in the MIME type — without a BOM it decodes the file
    // as ANSI and shows "SÃ£o Paulo". CSV parsers ignore the BOM.
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'contacts_sample.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    toastChakra({
      title: "Sample CSV Downloaded",
      description: "Use this sample format to upload your contacts",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopProgressPolling();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ============= PERMISSIONS =============
  const listPermission = hasPermission("contact-list", permissions);
  const createPermission = hasPermission("contact-create", permissions);
  const manageTagPermission = hasPermission("contact-manage-tag", permissions);
  const uploadCSVPermission = hasPermission("contact-upload-csv", permissions);
  const downloadCSVPermission = hasPermission("contact-download-smp-csv", permissions);
  const editPermission = hasPermission("contact-edit", permissions);
  const deletePermission = hasPermission("contact-delete", permissions);

  // ============= STATS =============
  const stats = [
    {
      title: "Total Contacts",
      value: pagination.total?.toLocaleString(),
      subtitle: "All contacts in database",
      icon: FiUsers
    },
    {
      title: "Total Tags",
      value: tags.length?.toLocaleString(),
      subtitle: "Custom organization tags",
      icon: FiTag
    },
    {
      title: "Tagged Contacts",
      value: pagination.tagedContact?.toLocaleString(),
      subtitle: `Tagged contacts`,
      icon: FiTag
    },
    {
      title: "Filtered Results",
      value: contacts.length?.toLocaleString(),
      subtitle: "Matching current filters",
      icon: FiFilter
    }
  ];

  // ============= RENDER =============
  if (error) {
    return (
      <>
        <Header />
        <div style={{ padding: '20px 65px' }}>
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button mt={4} onClick={() => window.location.reload()} colorScheme="white" background={"var(--brand-primary)"}>
            Retry
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <UploadProgressModal
        isOpen={uploadProgress.isUploading}
        onClose={closeProgressModal}
        progress={uploadProgress}
      />

      <BulkTagModal
        isOpen={isBulkTagOpen}
        onClose={onBulkTagClose}
        tags={tags}
        selectedContacts={selectedContacts}
        contacts={contacts}
        onAssignTags={handleBulkTagAssignment}
        isLoading={isLoading}
      />

      <Header header={header} />
      <BoxWrapper>
        <div>
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" mb={2}>Contacts Management</Heading>
              <Text color="gray.600">Manage customer contacts and organize them with custom tags & cohorts</Text>
            </Box>
            <HStack spacing={3}>
              {manageTagPermission && (
                <Button leftIcon={<FiTag size={16} />} variant="outline" onClick={onTagsOpen} className='updated-chakra-button' size={'sm'}>
                  Manage Tags
                </Button>
              )}

              {(uploadCSVPermission || downloadCSVPermission) && (
                <CSVActionsMenu
                  onUpload={uploadContacts}
                  onDownload={downloadSampleCSV}
                  uploadCSVPermission={uploadCSVPermission}
                  downloadCSVPermission={downloadCSVPermission}
                  isUploading={uploadProgress.isUploading}
                />
              )}

              {createPermission && (
                <Button leftIcon={<FiPlus size={16} />} colorScheme="blue" onClick={onAddOpen} className='updated-chakra-button' size={'sm'}>
                  Add Contact
                </Button>
              )}
            </HStack>
          </Flex>

          {listPermission && (
            <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={6} mb={8}>
              {stats.map((stat, index) => (
                <GridItem key={index}>
                  <StatsCard {...stat} isLoading={isLoading} />
                </GridItem>
              ))}
            </Grid>
          )}

          {listPermission && (
            <Box mb={8}>
              <SearchAndFilter
                onSearch={handleSearch}
                onTagFilter={handleTagFilter}
                tags={tags}
                isLoading={isLoading}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                currentTagFilter={currentTagFilter}
                whatsappFilter={whatsappFilter}
                setWhatsappFilter={handleWhatsappFilter}
                subscriberFilter={subscriberFilter}
                setSubscriberFilter={handleSubscriberFilter}
                cohortFilter={cohortFilter}
                setCohortFilter={handleCohortFilter}
              />
            </Box>
          )}

          {selectedContacts.length > 0 && (
            <Box className='box-style-card' mb={8}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="blue.800">
                  {selectedContacts.length} contact{selectedContacts.length > 1 ? 's' : ''} selected
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    leftIcon={<FiEdit />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={onBulkTagOpen}
                    className='updated-chakra-button'
                  >
                    Assign Tags
                  </Button>
                </HStack>
              </Flex>
            </Box>
          )}

          <ContactsTable
            contacts={contacts}
            onEdit={handleEditContact}
            onDelete={handleDeleteContact}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={handlePageChange}
            selectedContacts={selectedContacts}
            onSelectContact={handleSelectContact}
            onSelectAll={handleSelectAll}
            editPermission={editPermission}
            deletePermission={deletePermission}
            listPermission={listPermission}
            countries={countries}
          />

          <AddContactModal
            isOpen={isAddOpen}
            onClose={onAddClose}
            tags={tags}
            onSave={handleSaveContact}
            isLoading={isLoading}
          />

          <EditContactModal
            isOpen={isEditOpen}
            onClose={onEditClose}
            contact={selectedContact}
            tags={tags}
            onSave={handleUpdateContact}
            isLoading={isLoading}
          />

          <ManageTagsModal
            isOpen={isTagsOpen}
            onClose={onTagsClose}
            tags={tags}
            onCreateTag={handleCreateTag}
            onDeleteTag={handleDeleteTag}
            isLoading={isLoading}
          />
        </div>
      </BoxWrapper>
    </>
  );
};

export default ContactsManagement;